import { GripHorizontal, Info, Trash } from "lucide-react";
import { useRef, useState } from "react";
import { Field, Form } from "react-final-form";
import { OnChange } from "react-final-form-listeners";
import { ReactSortable } from "react-sortablejs";
import { Badge, Button, Card, CardBody, Col, Container, Modal, ModalBody, ModalHeader, Row, Tooltip } from "reactstrap";
import { DetailedErrorAlert } from "../../../components/DetailedErrorAlert";
import { LoadingButton } from "../../../components/LoadingButton";
import { PersistantReactTable } from "../../../components/grid/PersistantReactTable";
import { BlankableField } from "../../../components/inputs/final_form/BlankableField";
import { SelectInput } from "../../../components/inputs/final_form/SelectInput";
import { TextInput } from "../../../components/inputs/final_form/TextInput";
import { adminApi as api, extractErrorMessage } from "../../../util/api";
import { humanizeString } from "../../../util/formatters";
import { AdministrateSelectInput } from "../../components/inputs/AdministrateSelectInput";
import { makeColumns } from "../../utils";

const MOCKED_DATA = [
  {
    public_id: "XXXXX-1",
    current_workflow: "Prior Auth",
    created_on: "2024-07-22",
    updated_on: "2024-07-22",
    workflow_state: "Prior Auth",
    current_step: "Unstarted",
    status: "Received",
    substatus: "Other",
    payor_paths: "U",
    assignee: "example@careviso.com",
    patient_name: "John Doe",
    patient_dob: "2024-07-22",
    physician_name: "Dr. Who",
    physician_npi: "111111111",
    physician_practice: "Example Practice",
    lab: "Example Lab",
    lab_patient_id: "X-1",
    lab_order_id: "XX-1",
    offshore_restrictions: "No",
    collection_date: "2024-07-22",
    date_of_service: "2024-07-22",
    task_due: "2024-07-22",
    verified_payor: "AAA - Example Payor",
    insurance_name: "AAA - Example Insurance",
    member_id: "X-123",
    last_updated_on: "2024-07-22",
  },
  {
    public_id: "XXXXX-2",
    current_workflow: "Prior Auth",
    created_on: "2024-07-22",
    updated_on: "2024-07-22",
    workflow_state: "Prior Auth",
    current_step: "Unstarted",
    status: "Received",
    substatus: "Other",
    payor_paths: "U",
    assignee: "example@careviso.com",
    patient_name: "Jane Doe",
    patient_dob: "2024-07-22",
    physician_name: "Dr. Who",
    physician_npi: "111111111",
    physician_practice: "Example Practice",
    lab: "Example Lab",
    lab_patient_id: "X-2",
    lab_order_id: "XX-2",
    offshore_restrictions: "No",
    collection_date: "2024-07-22",
    date_of_service: "2024-07-22",
    task_due: "2024-07-22",
    verified_payor: "AAA - Example Payor",
    insurance_name: "AAA - Example Insurance",
    member_id: "X-124",
    last_updated_on: "2024-07-22",
  },
];

const CONFIGURED_PAYOR_PATHS = window.configuredPayorPaths;

const PAYOR_PATH_OPTIONS = CONFIGURED_PAYOR_PATHS.map((el) => ({
  label: el.path,
  value: el.path,
}));

const FILTER_OPTIONS = {
  public_id: {
    label: "Public ID",
    value: "public_id",
    description: "Case Public ID.",
    disablePrefill: true,
  },
  current_workflow: {
    label: "Current Workflow",
    value: "current_workflow",
    description: "Case current Workflow (PA or BI).",
    disablePrefill: true,
  },
  current_step: {
    label: "Current Step",
    value: "current_step",
    description: 'Case current step. E.g.: "Select Payor".',
    disablePrefill: true,
  },
  status: {
    label: "Current Status",
    value: "status",
    description: "Case current Status.",
    PrefillComponent: (props) => (
      <AdministrateSelectInput {...props} resourceUrl="/admin/enums/prior_auth_statuses.json" valueKey="id" labelKey="label" />
    ),
  },
  substatus: {
    label: "Current Substatus",
    value: "substatus",
    description: "Case current Substatus.",
    PrefillComponent: (props) => (
      <AdministrateSelectInput {...props} resourceUrl="/admin/enums/prior_auth_substatuses.json" valueKey="value" labelKey="label" />
    ),
  },
  payor_paths: {
    label: "Payor Paths",
    value: "payor_paths",
    description: "Insurance routing Payor Paths.",
    PrefillComponent: (props) => <SelectInput {...props} options={PAYOR_PATH_OPTIONS} />,
  },
  assignee: {
    label: "Assignee",
    value: "assignee",
    description: "User assigned to work on the Case.",
    disablePrefill: true,
  },
  patient_name: {
    label: "Patient Name",
    value: "patient_name",
    description: "Patient Name.",
    disablePrefill: true,
  },
  patient_dob: {
    label: "Patient DOB",
    value: "patient_dob",
    description: "Patient Date of Birth.",
    disablePrefill: true,
  },
  physician_name: {
    label: "Physician Name",
    value: "physician_name",
    description: "Provider Name.",
    disablePrefill: true,
  },
  physician_npi: {
    label: "Physician NPI",
    value: "physician_npi",
    description: "Provider NPI.",
    disablePrefill: true,
  },
  physician_practice: {
    label: "Physician Practice",
    value: "physician_practice",
    description: "Provider Practice.",
    disablePrefill: true,
  },
  lab: {
    label: "Lab",
    value: "lab",
    description: "Lab.",
    disablePrefill: true,
  },
  lab_patient_id: {
    label: "Lab Patient ID",
    value: "lab_patient_id",
    description: "Lab Patient ID.",
    disablePrefill: true,
  },
  lab_order_id: {
    label: "Lab Order ID",
    value: "lab_order_id",
    description: "Lab Order ID.",
    disablePrefill: true,
  },
  offshore_restrictions: {
    label: "Offshore Restrictions",
    value: "offshore_restrictions",
    description: "Case has Offshore Restrictions.",
    disablePrefill: true,
  },
  collection_date: {
    label: "Collection Date",
    value: "collection_date",
    description: "Case Collection Date.",
    disablePrefill: true,
  },
  date_of_service: {
    label: "Date of Service",
    value: "date_of_service",
    description: "Date of Service.",
    disablePrefill: true,
  },
  task_due: {
    label: "Task Due",
    value: "task_due",
    description: "Case task due date.",
    disablePrefill: true,
  },
  verified_payor: {
    label: "Verified Payor",
    value: "verified_payor",
    description: "Patient verified Insurance.",
    disablePrefill: true,
  },
  insurance_name: {
    label: "Insurance Name",
    value: "insurance_name",
    description: "Provided Insurance Name.",
    disablePrefill: true,
  },
  member_id: {
    label: "Member ID",
    value: "member_id",
    description: "Member ID.",
    disablePrefill: true,
  },
  last_updated_on: {
    label: "Last Updated On",
    value: "last_updated_on",
    description: "Last time Case got updated.",
    disablePrefill: true,
  },
  created_on: {
    label: "Created On",
    value: "created_on",
    description: "Case creation date.",
    disablePrefill: true,
  },
  preprocessing_categories: {
    label: "Preprocessing Categories",
    value: "preprocessing_categories",
    description: "Preprocessing Categories.",
    PrefillComponent: (props) => (
      <AdministrateSelectInput {...props} resourceUrl="/admin/preprocessing_categories.json" isMulti valueKey="id" labelKey="name" />
    ),
    disablePrefill: false,
  },
};

const FILTER_PRESETS = {
  minimal: {
    color: "primary",
    schema:
      '[{"prefilled_value":"","column_name":"status"},{"prefilled_value":"","column_name":"lab"},{"prefilled_value":"","column_name":"public_id"},{"prefilled_value":"","column_name":"created_on"},{"prefilled_value":"","column_name":"verified_payor"}]',
    label: "Minimal",
    description: "Minimal View (Status, Lab, Public ID, Created On, Verified Payor).",
  },
  all: {
    color: "secondary",
    schema:
      '[{"prefilled_value":"","column_name":"public_id"},{"prefilled_value":"","column_name":"current_workflow"},{"prefilled_value":"","column_name":"current_step"},{"prefilled_value":"","column_name":"status"},{"prefilled_value":"","column_name":"substatus"},{"prefilled_value":"","column_name":"payor_paths"},{"prefilled_value":"","column_name":"assignee"},{"prefilled_value":"","column_name":"patient_name"},{"prefilled_value":"","column_name":"patient_dob"},{"prefilled_value":"","column_name":"physician_name"},{"prefilled_value":"","column_name":"physician_npi"},{"prefilled_value":"","column_name":"physician_practice"},{"prefilled_value":"","column_name":"lab"},{"prefilled_value":"","column_name":"lab_patient_id"},{"prefilled_value":"","column_name":"lab_order_id"},{"prefilled_value":"","column_name":"offshore_restrictions"},{"prefilled_value":"","column_name":"collection_date"},{"prefilled_value":"","column_name":"date_of_service"},{"prefilled_value":"","column_name":"task_due"},{"prefilled_value":"","column_name":"verified_payor"},{"prefilled_value":"","column_name":"insurance_name"},{"prefilled_value":"","column_name":"member_id"},{"prefilled_value":"","column_name":"last_updated_on"},{"prefilled_value":"","column_name":"created_on"}]',
    label: "All",
    description: "All available columns.",
  },
  none: {
    color: "secondary",
    schema: "[]",
    label: "None",
    description: "Empty, configurable.",
  },
};

const validateColumn = (data, rowIndex) => (value) => {
  if (data && data?.value.some((column, index) => column.column_name === value && rowIndex !== index)) return "Column already selected.";

  return undefined;
};

const FilterOption = ({ option }) => {
  const [open, setTooltipOpen] = useState(false);

  const toggle = () => setTooltipOpen(!open);

  return (
    <>
      <Badge color={option?.disablePrefill ? "primary" : "success"} className="me-2 mb-1" id={option.value}>
        {option.label}

        {option?.isSlow && <span className="ms-1 p-1 d-inline-block align-bottom bg-warning bg-opacity-75 rounded-circle" />}
      </Badge>
      <Tooltip isOpen={open} target={option.value} toggle={toggle}>
        <p className="mb-0">{option?.description}</p>

        <small>{option?.disablePrefill ? "Can't be prefilled" : "Can be prefilled"}</small>
      </Tooltip>
    </>
  );
};

const PresetElement = ({ preset, index, setSchemaFn }) => {
  const [open, setTooltipOpen] = useState(false);

  const toggle = () => setTooltipOpen(!open);

  return (
    <>
      <Button id={preset.label} onClick={() => setSchemaFn(preset)} className="me-2 bg-opacity-75" key={index} color="dark">
        {preset.label}
      </Button>

      <Tooltip isOpen={open} target={preset.label} toggle={toggle}>
        <p>{preset.description}</p>
      </Tooltip>
    </>
  );
};

const InputRow = ({ row, index, onChange, inputValue, onRemove }) => {
  const rowColumnType = FILTER_OPTIONS[row.column_name] || null;

  const columnNameChangeHandler = (name) => {
    onChange(index, "column_name", name);
  };

  const prefilledValueChangeHandler = (valueOrValues) => {
    if (valueOrValues?.length < 1) {
      return onChange(index, "prefilled_value", "");
    }

    onChange(index, "prefilled_value", valueOrValues);
  };

  return (
    <Row>
      <Col xs="5">
        <BlankableField
          isClearable
          name={`column-name-${row.column_name}-${index}`}
          component={SelectInput}
          placeholder="Select a column"
          options={Object.values(FILTER_OPTIONS)}
          value={row.column_name}
          initialValue={row.column_name}
          validate={validateColumn(inputValue, index)}
        />

        <OnChange name={`column-name-${row.column_name}-${index}`}>{columnNameChangeHandler}</OnChange>
      </Col>

      {rowColumnType && !rowColumnType.disablePrefill ? (
        <Col xs="5">
          <BlankableField
            name={`prefilled-value-${row.column_name}-${index}`}
            isClearable
            render={(props) => <rowColumnType.PrefillComponent {...props} />}
            placeholder="Select a value to prefill"
            value={row.prefilled_value}
            initialValue={row.prefilled_value}
          />

          <OnChange name={`prefilled-value-${row.column_name}-${index}`}>{prefilledValueChangeHandler}</OnChange>
        </Col>
      ) : (
        <Col xs="5">
          <div className="bg-secondary bg-opacity-10 mt-1 rounded-2">
            <p className="text-center py-1">
              {(!rowColumnType && "No column defined") || (rowColumnType.disablePrefill && "Prefilling Disabled")}
            </p>
          </div>
        </Col>
      )}

      <Col xs="1">
        <Button className="mt-1 btn-icon" color="danger" size="sm" title="Remove column..." type="button" onClick={onRemove}>
          <Trash size={16} />
        </Button>
      </Col>

      <Col xs="1">
        <Button className="mt-1 ms-1 btn-icon column-handle" color="secondary" size="sm" title="Rearrange column..." type="button">
          <GripHorizontal size={16} />
        </Button>
      </Col>
    </Row>
  );
};

const AddButton = ({ onAdd }) => {
  return (
    <Button color="secondary" title="Add a column..." block type="button" onClick={onAdd}>
      Add New Column
    </Button>
  );
};

const useColumns = makeColumns((columns) => [...columns]);

const PreviewModal = ({ data, ...rest }) => {
  const parsedData = data?.map((column) => ({
    id: column?.column_name || "example",
    accessor: column?.column_name || "example",
    Header: humanizeString(column?.column_name || "example"),
  }));

  const [open, setOpen] = useState(false);

  const toggleModal = () => setOpen((x) => !x);

  const tableRef = useRef();

  const COLUMNS = useColumns(parsedData);

  return (
    <>
      <Button title="Preview table Columns" className="ms-2" color="info" onClick={toggleModal} {...rest}>
        Preview
      </Button>

      <Modal size="xl" isOpen={open} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>Table Preview</ModalHeader>

        <ModalBody>
          <PersistantReactTable
            title="Example Records"
            ref={tableRef}
            columns={COLUMNS}
            data={MOCKED_DATA}
            pages={1}
            defaultPageSize={2}
            disableFilters={true}
            showPagination={false}
          />
        </ModalBody>
      </Modal>
    </>
  );
};

export const GroupsFiltersTab = ({ group, groupId, refresh }) => {
  const initialJSONValue = () => {
    const value = JSON.parse(group?.enabled_columns || "[]");

    return { value: [...value] };
  };

  const [inputValue, setInputValue] = useState(initialJSONValue());
  const [error, setError] = useState(null);

  const jsonValidValues = () => {
    const valid = inputValue.value.filter((v) => {
      return v.column_name;
    });

    return JSON.stringify(valid);
  };

  const onAdd = () => {
    const val = inputValue.value;
    val.push({ prefilled_value: "", column_name: "" });
    setInputValue({ value: val });
  };

  const onRemove = (form, idx) => {
    const val = inputValue.value;

    val.splice(idx, 1);
    setInputValue({ value: val });

    form.change("value", JSON.stringify(val));
  };

  const onSubmit = async (formParams) => {
    try {
      const params = {
        enabled_columns: formParams.group.enabledColumns,
      };

      const req = await api.post(`/admin/groups/${groupId}/update_ui_fields`, params);

      if (req.status == 200) {
        refresh();
      }
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const onChange = (idx, name, value) => {
    const val = inputValue.value;
    const row = val[idx];

    row[name] = value;

    setInputValue({ value: val });
  };

  const setSchema = (preset) => {
    const val = JSON.parse(preset.schema || "[]");

    setInputValue({ value: val });
  };

  const handleSort = (sortedValue) => {
    setInputValue((_prev) => ({ value: [...sortedValue] }));
  };

  return (
    <>
      <Container fluid>
        <Row>{error && <DetailedErrorAlert message={error} />}</Row>

        <Row>
          <Col xs="4">
            <Card className="p-3 bg-info bg-opacity-10 border-0">
              <p className="text-center">Available Columns for Cases Tool:</p>

              <small className="mb-2">Tip: Hover the column to see its description.</small>

              <Col>
                {Object.values(FILTER_OPTIONS).map((option) => (
                  <FilterOption option={option} key={option.value} />
                ))}
              </Col>
            </Card>
          </Col>

          <Col xs="8">
            <Form onSubmit={onSubmit}>
              {({ form, values, handleSubmit, dirty, submitting }) => (
                <Card className="p-3">
                  <div className="d-flex flex-column">
                    <div className="d-flex flex-row my-1">
                      {Object.values(FILTER_PRESETS).map((preset, index) => (
                        <PresetElement preset={preset} index={index} setSchemaFn={setSchema} key={`preset-${index}`} />
                      ))}
                    </div>

                    <small>
                      Tip: You can choose one of the presets and rearrange the order of columns.
                      {dirty && (
                        <Badge className="ms-2" color="warning">
                          Unsaved
                        </Badge>
                      )}
                    </small>
                  </div>

                  <Field name="group.enabledColumns" component={TextInput} type="hidden" initialValue={jsonValidValues()} />

                  <CardBody>
                    <Col>
                      <ReactSortable list={inputValue.value} setList={handleSort} swap delay={2} handle=".column-handle">
                        {inputValue.value &&
                          inputValue.value.map((row, i) => (
                            <div key={i}>
                              <InputRow
                                row={row}
                                index={i}
                                inputValue={inputValue}
                                onChange={onChange}
                                onRemove={() => onRemove(form, i)}
                                value={values}
                              />
                            </div>
                          ))}
                      </ReactSortable>

                      {inputValue.value && inputValue.value.length == 0 && (
                        <div className="p-4 bg-secondary bg-opacity-10 rounded-2 text-center">
                          <Info size={24} />
                          <p className="text-center mb-0">No columns configured.</p>
                        </div>
                      )}
                    </Col>
                  </CardBody>

                  <div className="d-flex flex-row justify-content-between">
                    <AddButton onAdd={onAdd} />

                    {inputValue.value && inputValue.value.length > 0 && <PreviewModal data={inputValue.value} />}

                    <LoadingButton
                      title="Save changes..."
                      className="ms-2"
                      loading={submitting}
                      onClick={handleSubmit}
                      color={dirty ? "primary" : "secondary"}
                      type="button"
                    >
                      Save
                    </LoadingButton>
                  </div>
                </Card>
              )}
            </Form>
          </Col>
        </Row>
      </Container>
    </>
  );
};
